import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  Server,
  LayoutDashboard,
  ChefHat,
  MonitorPlay,
  Truck,
  Boxes,
  Users,
  Building2,
  BarChart3,
  MessageSquareHeart,
  Megaphone,
  ShieldAlert,
  UtensilsCrossed,
  LogOut,
  User2,
  ShoppingBag,
  Store,
  Check,
  Globe,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useBranch, ALL_BRANCHES } from '@/lib/branch';

const SECTIONS = [
  // Cashier scope (per ops policy): payments + reports + feedback only.
  // Server Console and Admin Dashboard expose branch-wide settings that
  // belong to managers/owners — never cashiers. Route guards in App.js
  // enforce the same.
  { path: '/server', label: 'Server', icon: Server, roles: ['admin', 'owner'] },
  { path: '/admin', label: 'Admin', icon: LayoutDashboard, roles: ['admin', 'owner'] },
  { path: '/kds', label: 'KDS', icon: ChefHat, roles: ['kitchen', 'admin', 'owner'] },
  { path: '/pos', label: 'POS', icon: MonitorPlay, roles: ['cashier', 'admin', 'owner'] },
  { path: '/waiter', label: 'Waiter', icon: UtensilsCrossed, roles: ['waiter', 'admin', 'owner'] },
  { path: '/drivers', label: 'Drivers', icon: Truck, roles: ['driver', 'admin', 'owner'] },
  { path: '/inventory', label: 'Inventory', icon: Boxes, roles: ['admin', 'owner'] },
  { path: '/staff', label: 'Staff', icon: Users, roles: ['admin', 'owner'] },
  { path: '/branches-admin', label: 'Branches', icon: Building2, roles: ['admin', 'owner'] },
  { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'owner', 'cashier'] },
  { path: '/feedback-admin', label: 'Feedback', icon: MessageSquareHeart, roles: ['admin', 'owner', 'cashier'] },
  { path: '/marketing', label: 'Marketing', icon: Megaphone, roles: ['admin', 'owner'] },
  { path: '/audit', label: 'Audit', icon: ShieldAlert, roles: ['admin', 'owner'] },
];

// Where the City Grill logo link should send the user when clicked. Manager
// roles land in the Server Console (the unified ops view); operational roles
// go back to their primary workspace.
const HOME_BY_ROLE = {
  admin: '/server',
  owner: '/server',
  cashier: '/pos',
  kitchen: '/kds',
  waiter: '/waiter',
  driver: '/driver',
};

/**
 * Persistent staff navigation rail. Shown at the top of every staff-facing page so users
 * can jump between Server Console / Admin / KDS / POS / Staff / etc. with one tap.
 *
 * Variant 'dark' is used inside KDS for kitchen-friendly contrast.
 */
export function StaffRail({ variant = 'light' }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  if (!user) return null;
  const visible = SECTIONS.filter((s) => s.roles.includes(user.role));
  if (!visible.length) return null;
  const homePath = HOME_BY_ROLE[user.role] || '/server';
  const canSeeServerConsole = ['admin', 'owner'].includes(user.role);

  const isDark = variant === 'dark';
  return (
    <nav
      data-testid="staff-rail"
      className={
        `sticky top-0 z-40 w-full border-b ${
          isDark
            ? 'border-white/10 bg-[#1b1d22] text-white'
            : 'border-border bg-card'
        }`
      }
    >
      <div className="mx-auto flex max-w-[1600px] items-center gap-1 overflow-x-auto px-3 py-1.5">
        <Link to={homePath} data-testid="staff-rail-home-link" className="flex shrink-0 items-center gap-2 pr-3">
          <span className={`flex h-7 w-7 items-center justify-center rounded-md ${isDark ? 'bg-primary text-primary-foreground' : 'bg-primary text-primary-foreground'}`}>
            <UtensilsCrossed className="h-4 w-4" />
          </span>
          <span className="font-display text-sm font-semibold whitespace-nowrap">City Grill</span>
        </Link>
        <span className={`mx-1 h-5 w-px shrink-0 ${isDark ? 'bg-white/15' : 'bg-border'}`} />

        {visible.map((s) => {
          const active = loc.pathname === s.path;
          const Icon = s.icon;
          return (
            <Link
              key={s.path}
              to={s.path}
              data-testid={`staff-rail-${s.label.toLowerCase()}-link`}
              className={`flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : isDark
                  ? 'text-white/75 hover:bg-white/10 hover:text-white'
                  : 'text-foreground/70 hover:bg-secondary'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{s.label}</span>
            </Link>
          );
        })}

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <BranchSwitcher isDark={isDark} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="staff-rail-user-button"
                variant={isDark ? 'ghost' : 'secondary'}
                size="sm"
                className={`h-8 gap-1.5 px-2 ${isDark ? 'text-white hover:bg-white/10' : ''}`}
              >
                <User2 className="h-4 w-4" />
                <span className="hidden max-w-[140px] truncate sm:inline">{user.full_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user.full_name}</div>
                <div className="text-xs capitalize text-muted-foreground">{user.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="staff-rail-profile-menu-item">
                <User2 className="mr-2 h-4 w-4" /> My profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/menu')} data-testid="staff-rail-customer-menu-item">
                <ShoppingBag className="mr-2 h-4 w-4" /> Customer menu
              </DropdownMenuItem>
              {canSeeServerConsole && (
                <DropdownMenuItem onClick={() => navigate('/server')} data-testid="staff-rail-home-menu-item">
                  <Server className="mr-2 h-4 w-4" /> Server Console
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); }} data-testid="staff-rail-signout-menu-item">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}

/**
 * Helper: order-type badge for KDS / Admin / Server Console / Order Tracking.
 * Provides instant visual cue for delivery / pickup / dine-in.
 */
export function OrderTypeBadge({ orderType, tableNumber, size = 'md' }) {
  const cfg = {
    delivery: { label: 'DELIVERY', icon: Truck, color: 'bg-violet-600 text-white border-violet-700' },
    pickup: { label: 'PICKUP', icon: ShoppingBag, color: 'bg-blue-600 text-white border-blue-700' },
    dine_in: { label: 'DINE-IN', icon: ChefHat, color: 'bg-emerald-600 text-white border-emerald-700' },
  }[orderType] || { label: (orderType || 'order').toString().toUpperCase(), icon: ShoppingBag, color: 'bg-slate-600 text-white border-slate-700' };
  const Icon = cfg.icon;
  const sizeCls = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  return (
    <span data-testid={`order-type-badge-${orderType || 'unknown'}`} className={`inline-flex items-center gap-1 rounded-full border font-semibold tracking-wide ${cfg.color} ${sizeCls}`}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
      {tableNumber ? ` · TABLE ${tableNumber}` : ''}
    </span>
  );
}

/**
 * Branch switcher — shown in the right side of StaffRail.
 *  • Managers (admin/owner) get a dropdown listing every branch + "All branches"
 *  • Locked roles (kitchen/cashier/driver) see a read-only pill with their branch name
 */
export function BranchSwitcher({ isDark = false, dense = false }) {
  const { branches, activeId, activeBranch, isAllBranches, canSwitch, canSelectAll, setActive } = useBranch();
  if (!branches.length) return null;
  const label = isAllBranches
    ? 'All branches'
    : (activeBranch?.name || 'Select branch');
  const Icon = isAllBranches ? Globe : Store;
  const sizeCls = dense ? 'h-7 px-1.5 text-[11px]' : 'h-8 px-2 text-xs';

  if (!canSwitch) {
    // Locked role — show a non-interactive pill
    return (
      <span
        data-testid="branch-rail-locked"
        className={`inline-flex items-center gap-1.5 rounded-md border ${sizeCls} ${isDark ? 'border-white/15 bg-white/5 text-white/90' : 'border-border bg-secondary/60 text-foreground'} max-w-[180px]`}
        title={label}
      >
        <Store className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="truncate font-medium">{label}</span>
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="branch-switcher-button"
          variant={isDark ? 'ghost' : 'secondary'}
          size="sm"
          className={`gap-1.5 ${sizeCls} ${isDark ? 'text-white hover:bg-white/10' : ''} max-w-[200px]`}
        >
          <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="truncate">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Active restaurant
        </DropdownMenuLabel>
        {canSelectAll && (
          <DropdownMenuItem
            data-testid="branch-switcher-all"
            onClick={() => setActive(ALL_BRANCHES)}
            className="cursor-pointer"
          >
            <Globe className="mr-2 h-4 w-4" /> All branches
            {isAllBranches && <Check className="ml-auto h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {branches.map((b) => {
          const selected = !isAllBranches && b.id === activeId;
          return (
            <DropdownMenuItem
              key={b.id}
              data-testid="branch-switcher-item"
              onClick={() => setActive(b.id)}
              className="cursor-pointer"
            >
              <Store className="mr-2 h-4 w-4 text-primary/70" />
              <div className="flex flex-col leading-tight">
                <span className="font-medium">{b.name}</span>
                {b.address && <span className="text-[11px] text-muted-foreground">{b.address}</span>}
              </div>
              {selected && <Check className="ml-auto h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
